import { AppBarInteractivity } from "@1js/office-start-appbar-types";
import { BackgroundShadeStyle } from "./BackgroundShadeStyle";
import {
  behaviorFragment as LaunchTile_launchTileBehavior,
  dataFragment as LaunchTile_m365App,
} from "./LaunchTile.types";
import { graphql } from "./taggedNode";
import { fragment as TileBadge_badge } from "./TileBadge.types";
import { modelFragment as TileContextMenu_tileContextMenu } from "./TileContextMenu.types";
/**
 * The action handler to execute when shortcut is pressed
 *
 */
export type ShortcutHandler = (ev?: React.KeyboardEvent<HTMLElement>) => void;
/**
 * Props for the App Bar Control
 *
 * @export
 * @interface AppBarProps
 */
export interface AppBarProps {
  forwardRef?:
    | ((instance: AppBarInteractivity) => void)
    | React.MutableRefObject<AppBarInteractivity>
    | null;
  /**
   * An optional custom bundling-importing handler for hosts to handle retries, exponential backoff, etc.
   * @template T - Type of the prop for the default React control exported by the package.
   * @param importPromise - An barebones bundle import promise.
   * @returns A promise that exports a default React control for the given prop type.
   */
  customImportHandler?<T>(
    importPromise: Promise<{
      default: React.FC<T>;
    }>
  ): Promise<{
    default: React.FC<T>;
  }>;
  /**
   * Registers a shortcut handler for a launch tile
   *
   * @param index Index value of tile to assign
   * @param commandHandler Callback assigned when shortcut is activated
   */
  assignShortcutHandler?(index: number, handler: ShortcutHandler): void;
  /**
   * Unregisters the shortcut handler for a launch tile
   * @param index Index value of tile to unassign
   * @param handler Callback assigned to shortcut when it was activated
   */
  unassignShortcutHandler?(index: number, handler: ShortcutHandler): void;
  /**
   * Callback for when hoverenter on currently selected app's tile
   * TODO : Remove and replace functionality with nova commanding when implemented
   */
  onAppTileHoverEnter?: () => void;
  /**
   * Callback for when hoverleave on currently selected app's tile
   * TODO : Remove and replace functionality with nova commanding when implemented
   */
  onAppTileHoverLeave?: () => void;
  /**
   * Prop to indicate the background shade style that is to be applied to the
   * AppBar.
   */
  backgroundShade?: BackgroundShadeStyle;
  /**
   * The match media query that the App Bar should use to detecting view port dimension
   * changes for its responsive behavior.
   */
  matchMedia?: (query: string) => MediaQueryList;
  /**
   * The logo to be displayed in the AppBar.
   * This can be any React element, such as an image or a custom logo component.
   */
  brandLogo?: React.ReactElement;
  children?: React.ReactNode;
}
export const controlModelQuery = graphql`
  query AppBarControlQuery {
    views {
      appBarControl {
        id
        rootNavigationAriaLabel
        animateSelection
        enableMicaV2DesktopStyles
        enableMicaV2WebStyles
        enableMercuryDesignStyles
        enableOptimisticMutations
        appTilePositionEnabled
        impressionStabilizeTimeout
        enableAIHub
        enableBrandLogo
        enableVisualRefreshStyles
        enableRockSteadyForM365
        tileBehavior {
          transparent
          shouldRemoveLockedAppsFromPinnedApps
          shouldShowLabel
          dragDropEnabled
          overrideAppBarDefaultSort
          useOutlineIcon
          useSmallColorIcon
          tileContextMenu {
            ...TileContextMenu_tileContextMenu
          }
          ...LaunchTile_launchTileBehavior
        }
        flyoutBehavior {
          flyoutSize
          selectFlyoutButtonWhenNoAppsSelected
          isSuggestionsSectionEnabled
          isQuickAccessSectionEnabled
          isSeeAllButtonEnabled
          seeAllButtonIndex
          enabledFlyoutButtonBadgeType
          searchBehavior {
            isEnabled
            placeholderText
            debounceRate
            noResultsTitle
            noResultsMessage
            resultsSectionTitle
            quickAccessResultsSectionTitle
          }
        }
        appAcquisitionLinkBehavior {
          linkFormat
        }
        localizedStrings {
          flyoutButtonBadgeDescription
          flyoutButtonText
          flyoutButtonTitle
          flyoutEmptyStateMessage
          flyoutSuggestedAppsPrivacyLinkText
          flyoutSuggestedAppsTermsOfUseLinkText
          flyoutSuggestedAppsPermissionsLinkText
          flyoutSuggestedAppsAddButtonText
          flyoutSuggestedAppsSectionTitle
          flyoutInstalledAppsSectionAriaLabel
          flyoutSearchResultsAriaLiveAnnouncement
          flyoutSuggestedAppsSectionAriaLabel
          flyoutInstalledAppsSearchResultsAnnouncement
          flyoutSuggestedAppsSearchResultsAnnouncement
          flyoutInstalledAndSuggestedAppsSearchResultsAnnouncement
          flyoutQuickAccessSectionTitle
          flyoutQuickAccessSectionAriaLabel
          flyoutSeeAllButtonLabel
          onDragStartText
          onDragEndText
          onDragOverText
          onDragCancelText
          onDragAtTopText
          onDragAtBottomText
        }
        flyoutIcon {
          __typename
          ... on InternalIcon {
            name
          }
          ... on RemoteImage {
            src
            isFullBleed
          }
        }
        flyoutIconSelected {
          __typename
          ... on InternalIcon {
            name
          }
          ... on RemoteImage {
            src
            isFullBleed
          }
        }
      }
    }
  }
  ${LaunchTile_launchTileBehavior}
  ${TileContextMenu_tileContextMenu}
`;
/**
 * The GraphQL query representing the AppBar
 */
export const dataQuery = graphql`
  query AppBarQuery {
    m365Apps {
      id
      edges {
        node {
          __typename
          id
          name
          platformType
          isInUse
          brandingColor
          lockedIndex
          pinnedIndex
          anchoredIndex
          hideFromSearch
          badge {
            __typename
            ... on M365ApplicationNumericBadge {
              count
            }
            ...TileBadge_badge
          }
          ...LaunchTile_m365App
        }
      }
      appAcquisitionLinks {
        id
        text
        accessibleName
        icon {
          name
        }
      }
    }
  }
  ${LaunchTile_m365App}
  ${TileBadge_badge}
`;
/**
 * Graph QL mutation for pinning an M365 application.
 */
export const pinAppMutation = graphql`
  mutation AppBarPinAppMutation($applicationID: ID!) {
    pinM365Application(input: { applicationID: $applicationID }) {
      __typename
      updatedM365Applications {
        __typename
        id
        platformType
        pinnedIndex
      }
    }
  }
`;
/**
 * Graph QL mutation for unpinning an M365 application.
 */
export const unpinAppMutation = graphql`
  mutation AppBarUnpinAppMutation($applicationID: ID!) {
    unpinM365Application(input: { applicationID: $applicationID }) {
      __typename
      updatedM365Applications {
        __typename
        id
        platformType
        pinnedIndex
      }
    }
  }
`;
/**
 * Graph QL mutation for moving the pin positioning of an M365 application.
 */
export const movePinPositionMutation = graphql`
  mutation AppBarMovePinnedAppMutation(
    $applicationID: ID!
    $newPinnedIndex: Int!
  ) {
    movePinnedM365Application(
      input: { applicationID: $applicationID, newPinnedIndex: $newPinnedIndex }
    ) {
      updatedM365Applications {
        id
        platformType
        pinnedIndex
      }
    }
  }
`;
